/**
 * QUÉ DIJO LA IA DE UNA FOTO, PARA ENSEÑARLO (05-10-2026, plan cultivos SIEX F7,
 * «pantallas que faltaban»).
 *
 * Con la lista del cultivo (`analyze-pest-image` con candidatos) la respuesta ya no es
 * solo «una plaga» o «nada». Trae `outcome`:
 *   · pest       → una plaga (de la lista o, si la nombró de fuera, `outsideList`);
 *   · beneficial → un AUXILIAR (Orius, Nesidiocoris…): fauna útil, NO es una plaga;
 *   · abiotic    → fisiopatía, carencia, quemadura…: tampoco es una plaga;
 *   · healthy    → la planta se ve sana;
 *   · unclear    → la foto no deja saberlo.
 * Antes las tres pantallas que enseñan el resultado (la ficha del parte, el escáner y la
 * foto del asistente) lo pintaban todo como plaga o como «no se reconoció ninguna plaga»:
 * un Orius salía con su gravedad y el botón «Crear tratamiento», y «no se puede saber»
 * se leía como «no hay nada».
 *
 * Esto solo LEE: no elige ninguna plaga del parte ni enlaza ningún foco (F1/F6/F21 de
 * sanidad-focos: lo que propone la IA se dice que es suyo y nunca se confirma solo).
 * Un análisis viejo o del modo abierto (sin `outcome`) se lee como siempre: con nombre,
 * plaga; sin nombre o `identified: false`, «nada».
 *
 * PURO: sin red ni React.
 */
import { limpiarNombrePlaga } from "@/lib/pestDiagnosis";

export type DesenlaceIA = "plaga" | "auxiliar" | "abiotico" | "sana" | "no_se_sabe" | "nada";

export interface LecturaIA {
  desenlace: DesenlaceIA;
  /** El titular: el nombre limpio y, en un auxiliar, sin la coletilla «(auxiliar: no es plaga)». */
  nombre: string;
  /** Una plaga que NO está en la lista del cultivo (lo dice el servidor). */
  fueraDeLista: boolean;
  /** Una plaga que salió de la vigilancia regional de la lista. */
  vigilancia: boolean;
  /** Plaga, auxiliar o abiótico: hay un nombre que enseñar en la tarjeta. */
  conNombre: boolean;
  /** Solo una PLAGA puede acabar en un tratamiento (un auxiliar o una carencia, no). */
  esPlaga: boolean;
}

/** Lo mínimo de un análisis (el del parte, el del escáner o el del chat). */
export interface AnalisisParaLeer {
  identified?: boolean | null;
  pestName?: string | null;
  outcome?: string | null;
  outsideList?: boolean | null;
  listSource?: string | null;
}

/* La coletilla que pone el servidor a un auxiliar (`sufijoAuxiliar` de analyze-pest-image,
   en los cuatro idiomas que sabe la función). Va entre paréntesis y sin coma, para que
   `limpiarNombrePlaga` no la corte; aquí se quita porque lo dice la etiqueta. */
const COLETILLA_AUXILIAR = /\s*\((?:auxiliar|beneficial|ausiliare|كائن نافع)[^)]*\)\s*$/iu;

/** «Orius laevigatus (auxiliar: no es plaga)» → «Orius laevigatus». */
export function quitarColetillaAuxiliar(nombre: string | null | undefined): string {
  return String(nombre ?? "").replace(COLETILLA_AUXILIAR, "").trim();
}

const DESENLACES: Record<string, DesenlaceIA> = {
  pest: "plaga",
  beneficial: "auxiliar",
  abiotic: "abiotico",
  healthy: "sana",
  unclear: "no_se_sabe",
};

/** `null` = todavía no hay análisis. */
export function leerDesenlace(a: AnalisisParaLeer | null | undefined): LecturaIA | null {
  if (!a) return null;
  const limpio = limpiarNombrePlaga(a.pestName);
  const tieneColetilla = COLETILLA_AUXILIAR.test(limpio);
  let desenlace: DesenlaceIA;
  const dicho = typeof a.outcome === "string" ? DESENLACES[a.outcome] : undefined;
  if (dicho) desenlace = dicho;
  else if (tieneColetilla && a.identified !== false) desenlace = "auxiliar";
  else desenlace = a.identified === false || !limpio ? "nada" : "plaga";
  // Una salida con nombre (auxiliar, abiótico) sin nombre que enseñar se queda sin tarjeta.
  const nombre = desenlace === "auxiliar" ? quitarColetillaAuxiliar(limpio) : limpio;
  const conNombre = (desenlace === "plaga" || desenlace === "auxiliar" || desenlace === "abiotico") && !!nombre;
  return {
    desenlace,
    nombre,
    fueraDeLista: desenlace === "plaga" && a.outsideList === true,
    vigilancia: desenlace === "plaga" && a.listSource === "watch",
    conNombre,
    esPlaga: desenlace === "plaga",
  };
}
